(() => {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const STATIC_RGB = Object.freeze({
    White: [255, 255, 255],
    Red: [255, 38, 48],
    Blue: [46, 70, 245],
    Green: [42, 210, 99],
    Yellow: [255, 217, 42],
    'Ice Blue': [76, 216, 255],
    Purple: [181, 79, 255]
  });
  const STATIC_COLORS = new Set(Object.keys(STATIC_RGB));
  const DATA_SELECTOR = 'script[type="application/json"][data-lds-badge-card-light]';
  const CARD_SELECTOR = 'hdt-card-product, .lds-plate-collection__product';
  const TARGET_SELECTOR = '.hdt-card-product__media-wrapp, .lds-plate-collection__product-image';
  const states = new Map();
  const targetStates = new WeakMap();
  const imageCache = new Map();
  const raf = callback => window.requestAnimationFrame ? window.requestAnimationFrame(callback) : window.setTimeout(() => callback(Date.now()), 50);
  const caf = id => window.cancelAnimationFrame ? window.cancelAnimationFrame(id) : window.clearTimeout(id);
  const now = () => window.performance?.now ? window.performance.now() : Date.now();
  const mql = query => window.matchMedia ? window.matchMedia(query) : null;
  const finePointer = mql('(hover: hover) and (pointer: fine)');
  const reducedMotion = mql('(prefers-reduced-motion: reduce)');

  const asNumber = value => Number.isFinite(Number(value)) ? Number(value) : 0;
  const asDimension = value => {
    const number = Math.round(Number(value));
    return Number.isFinite(number) && number > 0 ? number : 0;
  };
  const asRGB = value => {
    if (!Array.isArray(value) || value.length < 3 || value.slice(0, 3).some(channel => !Number.isFinite(Number(channel)))) return [255, 255, 255];
    return value.slice(0, 3).map(Number);
  };
  const isFineMouse = event => {
    if (event?.pointerType && event.pointerType !== 'mouse') return false;
    return finePointer ? finePointer.matches : true;
  };
  const isElementVisible = element => {
    if (!element?.isConnected) return false;
    const rect = element.getBoundingClientRect?.();
    if (!rect) return true;
    if (!rect.width || !rect.height) return false;
    const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;
    return rect.bottom > 0 && rect.right > 0 && rect.top < viewportHeight && rect.left < viewportWidth;
  };
  const addMediaListener = (query, listener) => {
    if (!query) return () => {};
    if (query.addEventListener) query.addEventListener('change', listener);
    else query.addListener?.(listener);
    return () => {
      if (query.removeEventListener) query.removeEventListener('change', listener);
      else query.removeListener?.(listener);
    };
  };
  const parseData = node => {
    try {
      const value = JSON.parse(node.textContent || 'null');
      return value && typeof value === 'object' ? value : null;
    } catch {
      return null;
    }
  };
  const getCard = node => node?.closest?.(CARD_SELECTOR) || null;
  const getTarget = card => card?.querySelector?.(TARGET_SELECTOR) || null;
  const getNativeImage = target => target?.querySelector?.('img:not([data-lds-badge-card-light-canvas])') || null;
  const stopAnimation = state => {
    if (state.animation) caf(state.animation);
    state.animation = 0;
  };
  const hideCanvas = state => {
    if (state.canvas) state.canvas.hidden = true;
    state.active = false;
    state.frame = null;
  };
  const showCanvas = state => {
    if (!state.canvas) return false;
    state.canvas.hidden = false;
    state.active = true;
    return true;
  };
  const loadImage = source => {
    if (!source || typeof source !== 'string') return Promise.reject(new Error('missing-image-source'));
    if (!imageCache.has(source)) {
      imageCache.set(source, new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => {
          imageCache.delete(source);
          reject(new Error('image-load'));
        };
        image.src = source;
      }));
    }
    return imageCache.get(source);
  };
  const hue = (h, n) => {
    const k = (n + h * 12) % 12;
    return Math.round(255 * (.57 - .43 * Math.max(-1, Math.min(k - 3, 9 - k, 1))));
  };

  const parseState = (node, card, text) => {
    const data = parseData(node);
    const id = String(data?.id ?? card?.dataset?.ldsProductId ?? card?.dataset?.productId ?? '');
    const config = data?.config && typeof data.config === 'object' ? data.config : null;
    const color = typeof data?.color === 'string' ? data.color : '';
    const legacy = Boolean(config && !config.source && typeof config.White === 'string');
    const source = typeof config?.source === 'string' ? config.source : (typeof config?.White === 'string' ? config.White : (typeof config?.originalSource === 'string' ? config.originalSource : ''));
    const flowSource = typeof config?.source === 'string' ? config.source : (typeof config?.White === 'string' ? config.White : '');
    const rawBBox = config?.bbox || config?.maskBBox;
    const bbox = Array.isArray(rawBBox) && rawBBox.length === 4 ? rawBBox.map(asNumber) : null;
    const width = asDimension(config?.width) || 1254;
    const height = asDimension(config?.height) || 1254;
    const validBBox = Boolean(bbox && bbox.every(Number.isFinite) && bbox[2] > 0 && bbox[3] > 0 && bbox[0] >= 0 && bbox[1] >= 0);
    const knownColor = STATIC_COLORS.has(color) || color === 'Flow';
    const staticSource = legacy && STATIC_COLORS.has(color) && typeof config[color] === 'string' ? config[color] : '';
    const renderSource = staticSource || flowSource;
    const valid = Boolean(config && config.enabled !== false && typeof config.mask === 'string' && config.mask && validBBox && renderSource);
    const error = !data ? 'invalid-json' : !config ? 'missing-config' : config.enabled === false ? 'disabled' : !knownColor ? 'unknown-color' : !valid ? 'invalid-config' : null;

    return {
      node,
      card,
      target: getTarget(card),
      nativeImage: getNativeImage(getTarget(card)),
      text,
      id,
      color,
      config,
      legacy,
      source,
      flowSource,
      staticSource,
      bbox,
      width,
      height,
      sourceRGB: asRGB(config?.sourceRGB),
      valid: valid && knownColor,
      error,
      canvas: null,
      prepared: null,
      staticImage: null,
      hovered: false,
      active: false,
      ready: false,
      frame: null,
      animation: 0,
      revision: 0,
      lastFrame: 0,
      visible: true,
      cleanups: []
    };
  };

  const ensureCanvas = state => {
    if (state.canvas || !state.target) return state.canvas;
    const canvas = document.createElement('canvas');
    canvas.className = 'lds-badge-card-light__canvas';
    canvas.dataset.ldsBadgeCardLightCanvas = '';
    canvas.width = state.width;
    canvas.height = state.height;
    canvas.hidden = true;
    canvas.setAttribute('aria-hidden', 'true');
    state.target.classList.add('lds-badge-card-light__target');
    state.target.append(canvas);
    state.canvas = canvas;
    return canvas;
  };

  const prepareBlend = async state => {
    if (state.prepared) return state.prepared;
    const [base, mask] = await Promise.all([loadImage(state.flowSource || state.source), loadImage(state.config.mask)]);
    const cropWidth = Math.round(state.bbox[2]);
    const cropHeight = Math.round(state.bbox[3]);
    const crop = document.createElement('canvas');
    crop.width = cropWidth;
    crop.height = cropHeight;
    const cropContext = crop.getContext('2d', { willReadFrequently: true });
    if (!cropContext) throw new Error('canvas-context');
    cropContext.drawImage(base, state.bbox[0], state.bbox[1], cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
    const pixels = cropContext.getImageData(0, 0, cropWidth, cropHeight);
    cropContext.clearRect(0, 0, cropWidth, cropHeight);
    cropContext.drawImage(mask, 0, 0, cropWidth, cropHeight);
    const maskPixels = cropContext.getImageData(0, 0, cropWidth, cropHeight).data;
    state.prepared = { base, pixels, maskPixels, bbox: state.bbox, cropWidth, cropHeight };
    return state.prepared;
  };

  const renderPixels = (state, prepared, rgb, time) => {
    const output = new ImageData(new Uint8ClampedArray(prepared.pixels.data), prepared.cropWidth, prepared.cropHeight);
    for (let index = 0; index < output.data.length; index += 4) {
      const core = prepared.maskPixels[index] / 255;
      const halo = prepared.maskPixels[index + 1] / 255;
      if (!core && !halo) continue;
      let colorRGB = rgb;
      if (time !== null) {
        const localX = (index / 4) % prepared.cropWidth;
        const flowHue = ((localX / (state.legacy ? 350 : prepared.cropWidth * 350 / 427) - time / 6500) % 1 + 1) % 1;
        colorRGB = [hue(flowHue, 0), hue(flowHue, 8), hue(flowHue, 4)];
      }
      for (let channel = 0; channel < 3; channel++) {
        // Approved blend: original crop RGB + (color RGB - source RGB) * core + color RGB * halo.
        output.data[index + channel] = prepared.pixels.data[index + channel] + (colorRGB[channel] - state.sourceRGB[channel]) * core + colorRGB[channel] * halo;
      }
    }
    return output;
  };

  const drawBlend = (state, prepared, rgb, time) => {
    const canvas = ensureCanvas(state);
    if (!canvas) throw new Error('canvas-missing');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas-context');
    context.clearRect(0, 0, state.width, state.height);
    context.drawImage(prepared.base, 0, 0, state.width, state.height);
    context.putImageData(renderPixels(state, prepared, rgb, time), Math.round(prepared.bbox[0]), Math.round(prepared.bbox[1]));
    state.frame = time === null ? null : time;
    canvas.dataset.frame = time === null ? '' : String(time);
  };

  const drawStaticImage = (state, image) => {
    const canvas = ensureCanvas(state);
    if (!canvas) throw new Error('canvas-missing');
    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas-context');
    context.clearRect(0, 0, state.width, state.height);
    context.drawImage(image, 0, 0, state.width, state.height);
    state.frame = null;
    canvas.dataset.frame = '';
  };

  const canAnimate = state => state.color === 'Flow' && state.hovered && state.active && !document.hidden && !(reducedMotion?.matches) && state.visible !== false && isElementVisible(state.target);
  const scheduleFlow = state => {
    stopAnimation(state);
    const tick = time => {
      state.animation = 0;
      if (!canAnimate(state)) return;
      if (time - state.lastFrame >= 50) {
        try {
          drawBlend(state, state.prepared, null, time);
          state.lastFrame = time;
        } catch {
          state.error = 'canvas-render';
          stopAnimation(state);
          return;
        }
      }
      if (canAnimate(state)) state.animation = raf(tick);
    };
    if (canAnimate(state)) state.animation = raf(tick);
  };

  const renderHover = async state => {
    const token = ++state.revision;
    state.error = null;
    state.ready = false;
    stopAnimation(state);
    try {
      if (state.color === 'Flow') {
        if (reducedMotion?.matches) {
          if (state.legacy && typeof state.config.White === 'string') {
            state.staticImage = state.staticImage || await loadImage(state.config.White);
            if (token !== state.revision || !state.hovered) return;
            drawStaticImage(state, state.staticImage);
          } else {
            const prepared = await prepareBlend(state);
            if (token !== state.revision || !state.hovered) return;
            drawBlend(state, prepared, STATIC_RGB.White, null);
          }
        } else {
          const prepared = await prepareBlend(state);
          if (token !== state.revision || !state.hovered) return;
          drawBlend(state, prepared, null, now());
        }
      } else if (state.staticSource) {
        state.staticImage = state.staticImage || await loadImage(state.staticSource);
        if (token !== state.revision || !state.hovered) return;
        drawStaticImage(state, state.staticImage);
      } else {
        const prepared = await prepareBlend(state);
        if (token !== state.revision || !state.hovered) return;
        drawBlend(state, prepared, STATIC_RGB[state.color], null);
      }
      if (token !== state.revision || !state.hovered) return;
      state.ready = true;
      showCanvas(state);
      if (state.color === 'Flow' && !(reducedMotion?.matches)) {
        state.lastFrame = now();
        scheduleFlow(state);
      }
    } catch (error) {
      if (token !== state.revision) return;
      state.error = error?.message === 'canvas-context' || error?.message === 'canvas-render' ? error.message : 'image-load';
      state.ready = false;
      hideCanvas(state);
    }
  };

  const enter = (state, event) => {
    if (!state.valid || !isFineMouse(event)) return;
    state.hovered = true;
    renderHover(state);
  };
  const leave = state => {
    if (!state.hovered) return;
    state.hovered = false;
    state.revision++;
    stopAnimation(state);
    hideCanvas(state);
  };
  const visibilityChanged = (state, visible) => {
    state.visible = visible;
    if (state.color !== 'Flow' || !state.hovered) return;
    if (!visible) {
      stopAnimation(state);
      return;
    }
    if (state.ready && state.active && !(reducedMotion?.matches)) scheduleFlow(state);
  };

  const bindState = state => {
    if (!state.valid || !state.target) return;
    state.target.classList.add('lds-badge-card-light__target');
    const onEnter = event => enter(state, event);
    const onLeave = () => leave(state);
    state.target.addEventListener('pointerenter', onEnter);
    state.target.addEventListener('pointerleave', onLeave);
    state.cleanups.push(() => state.target.removeEventListener('pointerenter', onEnter));
    state.cleanups.push(() => state.target.removeEventListener('pointerleave', onLeave));
    if (intersectionObserver) {
      targetStates.set(state.target, state);
      intersectionObserver.observe(state.target);
    }
  };

  const destroyState = state => {
    state.revision++;
    state.hovered = false;
    stopAnimation(state);
    state.cleanups.splice(0).forEach(cleanup => cleanup());
    if (intersectionObserver && state.target) intersectionObserver.unobserve(state.target);
    if (state.canvas) state.canvas.remove();
    if (state.target) state.target.classList.remove('lds-badge-card-light__target');
    states.delete(state.node);
  };

  const initNode = node => {
    if (!node.isConnected) return;
    const card = getCard(node);
    if (!card) return;
    const text = node.textContent || '';
    const existing = states.get(node);
    if (existing && existing.card === card && existing.text === text && existing.target === getTarget(card)) return;
    if (existing) destroyState(existing);
    const state = parseState(node, card, text);
    states.set(node, state);
    bindState(state);
  };

  const scan = () => {
    const nodes = [...document.querySelectorAll(DATA_SELECTOR)];
    const live = new Set(nodes);
    nodes.forEach(initNode);
    for (const state of [...states.values()]) {
      if (!live.has(state.node) || !state.node.isConnected || !state.card.isConnected || !state.target || !state.card.contains(state.target)) destroyState(state);
    }
  };

  const intersectionObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = targetStates.get(entry.target);
      if (state) visibilityChanged(state, entry.isIntersecting);
    });
  }, { threshold: 0 }) : null;

  window.__LDS_BADGE_CARD_LIGHTS__ = () => [...states.values()].filter(state => state.node.isConnected).map(state => ({
    id: state.id,
    ready: Boolean(state.ready),
    active: Boolean(state.active),
    hovered: Boolean(state.hovered),
    color: state.color,
    frame: state.frame,
    error: state.error
  }));

  const onMotionChange = () => {
    for (const state of states.values()) {
      if (state.color !== 'Flow' || !state.hovered || !state.ready) continue;
      stopAnimation(state);
      if (reducedMotion?.matches) {
        renderHover(state);
      } else if (state.active && state.prepared) {
        state.lastFrame = 0;
        scheduleFlow(state);
      } else if (state.active) {
        renderHover(state);
      }
    }
  };
  const removeMotionListener = addMediaListener(reducedMotion, onMotionChange);
  const removePointerListener = addMediaListener(finePointer, () => {
    if (finePointer?.matches) return;
    for (const state of states.values()) leave(state);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      for (const state of states.values()) stopAnimation(state);
    } else {
      for (const state of states.values()) if (state.color === 'Flow' && state.hovered && state.active && !(reducedMotion?.matches)) scheduleFlow(state);
    }
  }, { passive: true });
  window.addEventListener('pagehide', event => {
    for (const state of states.values()) leave(state);
    if (!event.persisted) {
      removeMotionListener();
      removePointerListener();
    }
  });

  scan();
  const observer = new MutationObserver(scan);
  observer.observe(document.documentElement || document, { childList: true, subtree: true, characterData: true });
})();
